import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import MasterViewPage from "../common/MasterViewPage";
import StatusBadge from "../common/StatusBadge";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import { getDistricts } from "../../services/districtService";
import { getStationById } from "../../services/stationService";
import { getSubDivisions } from "../../services/subDivisionService";
import { getMasterHierarchyAccess } from "../../utils/hierarchyScope";
import {
  buildLookupMap,
  extractApiErrorMessage,
  formatMasterDate,
} from "../../utils/masterHelpers";
import "./Stations.css";

export default function StationView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { loading: capabilitiesLoading, user } = useCapabilities();
  const [districtLookup, setDistrictLookup] = useState({});
  const [loading, setLoading] = useState(true);
  const [station, setStation] = useState(null);
  const [subDivisionLookup, setSubDivisionLookup] = useState({});
  const [toast, setToast] = useState({ message: "", open: false, severity: "error" });
  const scopeAccess = useMemo(
    () => getMasterHierarchyAccess({ user }),
    [user]
  );
  const { showDistrictField, showSubDivisionField } = scopeAccess;

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      if (!id) {
        setToast({
          message: "Station ID is missing.",
          open: true,
          severity: "error",
        });
        setStation(null);
        return;
      }

      const [districts, subDivisions, selectedStation] = await Promise.all([
        showDistrictField ? getDistricts() : Promise.resolve([]),
        showSubDivisionField ? getSubDivisions() : Promise.resolve([]),
        getStationById(id),
      ]);

      setDistrictLookup(buildLookupMap(districts));
      setSubDivisionLookup(buildLookupMap(subDivisions));

      if (!selectedStation) {
        setToast({
          message: "Station not found.",
          open: true,
          severity: "error",
        });
      }

      setStation(selectedStation || null);
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(apiError, "Failed to load station details."),
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id, showDistrictField, showSubDivisionField]);

  useEffect(() => {
    if (capabilitiesLoading) {
      return;
    }

    loadData();
  }, [capabilitiesLoading, loadData]);

  const fields = station
    ? [
        { label: "Code", value: station.code || "NA" },
        { label: "Name", value: station.name || "NA" },
        ...(showDistrictField
          ? [
              {
                label: "District",
                value:
                  station.district || districtLookup[station.district_id] || "NA",
              },
            ]
          : []),
        ...(showSubDivisionField
          ? [
              {
                label: "Sub-division",
                value: subDivisionLookup[station.sub_division_id] || "NA",
              },
            ]
          : []),
        { label: "State", value: station.state || "NA" },
        { label: "Status", value: <StatusBadge value={station.is_active} /> },
        { label: "Created At", value: formatMasterDate(station.created_at) },
      ]
    : [];

  return (
    <MasterShell pageClassName="stations-module">
      {loading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading station</h2>
          <p className="masters-state__text">
            Please wait while the station details are being loaded.
          </p>
        </div>
      ) : station ? (
        <MasterViewPage
          description="Station details with district and sub-division name mapping."
          fields={fields}
          onBack={() => navigate("/stations")}
          title="View Station"
        />
      ) : (
        <div className="masters-state">
          <h2 className="masters-state__title">Station not found</h2>
          <p className="masters-state__text">
            The selected station could not be found in the current response.
          </p>
        </div>
      )}

      <MasterToast
        message={toast.message}
        onClose={() => setToast((currentToast) => ({ ...currentToast, open: false }))}
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
